/**
 * Box Truck loadboard analytics — every event name starts with `box_truck_`, category BOX_TRUCK.
 *
 * Cardinality discipline (mirrors the Agentic Hub helpers):
 *   - No externalId / load id / broker email / free text on events.
 *   - Counts + lengths bucketed to fixed enum strings.
 * Analytics must never break the board — every call swallows errors silently.
 */
import axios from 'axios'
import {
    BOX_TRUCK_ASSET_DOC_TYPES,
    BOX_TRUCK_DOC_DIRECT_UPLOAD_MIN_BYTES,
    BOX_TRUCK_DOC_MAX_BYTES,
    BOX_TRUCK_OWNER_DOC_TYPES,
    BOX_TRUCK_PROFILE_DRIVER_DOC_TYPES,
    BOX_TRUCK_PROTECTED_DOC_TYPES,
    type BoxTruckCapacityLimits,
    type BoxTruckNearbyDriversSort,
    boxTruckDatSelection,
    CanonicalLoad,
    isDefaultBoxTruckCapacityLimits,
    sanitizeBoxTruckMaxPostedAgeHours,
} from '@numeo/types-core'
import { type BoxTruckPricingReportReason, SMS_MAX_IMMEDIATE_RECIPIENTS } from '@numeo/types'
import { AnalyticsEvent, EventCategory, trackUnifiedEvent } from 'services/analytics'
import type { TruckModalVariant } from 'store/trucksSlice'
import { LocationData } from 'features/load-hub/types/filters'
import { sourceGroupKey } from './loadFields'
import { classifyBoxTruckSaveFailure } from './boxTruckSaveFailure'
import { boxTruckDocumentRejectionReason } from './documentMimeTypes'
import { boxTruckDirectUploadFailedStep, needsDirectBoxTruckDocumentUpload } from '../services/boxTruckDirectDocumentUpload'
import type { BoxTruckDatSubtypeInteraction } from '@numeo/types-core'
import type { BoxTruckCapacityInteraction } from '../types/filterInteractions'
import type { AutofillProblem } from './documentAutofill/targets'

// ─── Bounded enums ─────────────────────────────────────────────────────────

/** A picked leg's shape: a city (has coords → radius search), a bare state, or nothing. */
type BoxTruckLegType = 'city' | 'state' | 'none'

/** The broker-email flavour composed from the drawer. */
export type BoxTruckEmailType = 'info' | 'offer' | 'book'

/** Smaller UI controls grouped under BOX_TRUCK_UI_INTERACTION (group by `element`). */
export type BoxTruckUiElement =
    /** The pricing panel settled on an answer for one load — see `trackBoxTruckPricingQuote`. */
    | 'pricing_quote'
    /** A dispatcher reported a suggestion as wrong — see `trackBoxTruckPricingReported`. */
    | 'pricing_reported'
    /**
     * A recipient-picking shortcut on the Mailing or SMS composer: `<service>:<preset>:<selected>`.
     *
     * Worth counting because the presets are a claim about what operators do — that they reach for
     * "everyone" and for "the ones the other service cannot carry". If `gap` is never pressed, the extra
     * column and the walk it costs are not paying for themselves.
     */
    | 'mailing_select_preset'
    /**
     * The "Send from" lines could not be read, so the picker is absent.
     *
     * Counted because its absence is indistinguishable from "you only have one line": without this a
     * Quo outage looks like a quiet UI and the operator sends believing they know which number it left
     * on. The series is how we tell one from the other.
     */
    | 'sender_lines_unavailable'
    /**
     * The SMS inbox was narrowed to one desk (`detail`: `all`, `group` or `ungrouped`).
     *
     * The KIND of bucket and never which one: a desk's name is the tenant's own vocabulary and has no
     * business in a third-party analytics tool. What the series answers is whether operators use the
     * filter at all, and whether the no-desk bucket — the conversations nobody on the roster answers to —
     * is the one they reach for, which would say something about the roster rather than the inbox.
     */
    | 'sms_desk_filter'
    /** The Driver Bids pill was opened (label `on`) — kept under its original name so the series does not
     *  break where the control became a pinned tab. */
    | 'bid_loads_toggled'
    /** The My Biddings pill was opened. Counted separately from `bid_loads_toggled` because the two answer
     *  different questions — what the drivers bid on, and what this dispatcher sent — so "which board do
     *  dispatchers actually work from" is only answerable per board. */
    | 'my_bids_opened'
    | 'source_toggled'
    /** The Advanced Filters switch that decides whether the board asks for live DAT at all — distinct from
     *  `source_toggled`, which only hides rows already fetched. */
    | 'dat_source_toggled'
    /** The same switch for BEON. Separate from `dat_source_toggled` because the two feeds cost different
     *  things — DAT spends Numeo's shared token pool, BEON the operator's own connected session — so "how
     *  many operators keep this on" has to be answerable per feed. */
    | 'beon_source_toggled'
    /** Connect pressed on the BEON row: the board offers only the extension capture, so this counts how
     *  often an operator starts that flow from here rather than from Load Hub. */
    | 'beon_connect_opened'
    | 'dat_subtypes_changed'
    | 'capacity_limit_changed'
    | 'capacity_unknown_toggled'
    | 'capacity_reset'
    | 'secondary_filters_reset'
    | 'advanced_filters_reset'
    | 'copy_load'
    /** The Nearby Drivers order changed by a header click — see `trackBoxTruckNearbySortChanged`. */
    | 'nearby_sort_changed'
    | 'map_opened'
    /** The inline route map drew for an opened load — the setting's actual usage, as against the Expand
     *  press `map_opened` counts. Both exist because the map moved back onto the open path for some
     *  operators: counting only Expand would read as map usage collapsing. */
    | 'map_drawn_on_open'
    /** The "Open map when a load is opened" switch, with `on`/`off` — the only measure of how many
     *  operators accept a route lookup per load opened. */
    | 'open_map_on_expand_toggled'
    | 'refresh_clicked'
    /** How a Search the operator pressed ended, with `arm` the endpoint it ran on — see `trackBoxTruckSearchOutcome`. */
    | 'search_outcome'
    | 'equipment_changed'
    | 'factoring_provider_changed'
    /** "See Rating" pressed — the drawer no longer looks a broker up on open, so this is the only
     *  signal for how often a rating is actually wanted per load opened. */
    | 'factoring_rating_revealed'
    | 'browser_tab_opened'
    /** "See company/vehicle documents" pressed. The panels fetch nothing until this happens, so it is the
     *  only signal for how often the attached records are actually wanted per driver opened. */
    | 'assigned_documents_requested'
    /** The same fetch was refused or never answered — without it a spike reads as "nobody uses this". */
    | 'assigned_documents_failed'
    /** "Send again" on one of our emails in the drawer's Email Chat, and the same resend failing — the failure
     *  is counted on its own so a broken resend shows up as failures, not as nobody using it. */
    | 'email_resent'
    | 'email_resend_failed'
    /** A document chip opened, on any of the three strips. */
    | 'document_previewed'
    /** The viewer could not display an opened document. `detail` is the kind of file — bounded, never the
     *  file name: `image` means the operator saw an error, `pdf` means pdf.js failed and the document was
     *  shown in the browser's own viewer instead. */
    | 'document_preview_failed'
    /** The driver detail could not resolve its owner and vehicle, and the operator's retry of it. */
    | 'driver_assignments_failed'
    | 'driver_assignments_retried'
    /** One settled full company/deposit read. Outcome and failure reason are bounded; no values or IDs. */
    | 'driver_financial_details_read'
    /** The detail panel's field search was used at all (once per open, not per keystroke), and its group
     *  filter. Both exist because 26 fields were hard to scan; neither is worth keeping if nobody uses them. */
    | 'driver_field_searched'
    | 'driver_group_filtered'
    /** The email template editor's design flow. A design applied, with `detail` the way it arrived
     *  (`TemplateDesignSource`); refused, with `detail` why (`TemplateDesignRefusal`); and the Replace
     *  confirm for a design pasted into Visual turned down. Together they say whether operators get designs in
     *  at all, and where they give up. */
    | 'template_design_applied'
    | 'template_design_refused'
    | 'template_design_replace_cancelled'
    | 'template_design_code_copied'
    /** "Edit with AI" used, with `detail` the assistant opened or `copy_prompt`. */
    | 'template_ai_opened'
    /** A picked profile document refused before upload. `detail` is `<reason>:<size bucket>` — never the file
     *  name — so it answers how often operators try files over the limit, and how far over. */
    | 'profile_document_skipped'
    /** The browser refused to let the page read the clipboard, so the paste button fell back to asking for
     *  Ctrl+V. Counted so a browser or permission change that breaks one-click paste shows up. */
    | 'template_clipboard_read_blocked'

/** Where Nearby Drivers is open: a load's drawer, or the Search Vehicles drawer, which has no load. */
export type BoxTruckNearbyDriversSurface = 'load_drawer' | 'search_vehicles'

/** How the first page of a newly clicked order ended. `abandoned`: something else changed (another click,
 *  the radius, the load) before it landed. Bounded. */
export type BoxTruckNearbySortOutcome = 'loaded' | 'failed' | 'abandoned'

/** How a design reached the template editor. Bounded: these are the only paste paths. */
export type TemplateDesignSource = 'ai_result' | 'code_button' | 'visual_paste'
/** Why a pasted design was refused. Bounded: the reasons `applyDesign` distinguishes. */
export type TemplateDesignRefusal = 'not_a_design' | 'unsafe_or_unknown_fields' | 'empty' | 'unexpected_error'

// ─── Bucketing helpers ─────────────────────────────────────────────────────

const legType = (loc: LocationData | null): BoxTruckLegType => {
    if (!loc) return 'none'
    return loc.latitude != null && loc.longitude != null ? 'city' : 'state'
}

const bucketCount = (n: number): '0' | '1' | '2+' => (n <= 0 ? '0' : n === 1 ? '1' : '2+')
/** Lanes are bucketed further than `bucketCount`: the cost difference between two and twelve searches is
 *  the thing worth seeing, and "2+" hides exactly that. Still a closed set. */
const bucketLanes = (n: number): '0' | '1' | '2-3' | '4-6' | '7+' => (n <= 0 ? '0' : n === 1 ? '1' : n <= 3 ? '2-3' : n <= 6 ? '4-6' : '7+')

// ─── Tracking entry points (take domain values, bucket internally) ──────────

type FireProps = Record<string, string | number | boolean>
/** `category` and `action` are the event's identity, set by `fire`: a property of either name would be dropped without a word. */
type OwnProps<P> = P & { category?: never; action?: never }

const fire = <P extends FireProps>(action: AnalyticsEvent, props?: OwnProps<P>) => {
    try {
        // Spread props FIRST so the event identity (category/action) always wins.
        trackUnifiedEvent({ ...props, category: EventCategory.BOX_TRUCK, action })
    } catch {
        // analytics must never break the board
    }
}

type BoxTruckVehicleSaveMethod = 'create' | 'patch'
export type BoxTruckVehicleSaveFailureStage = 'validation' | 'vehicle' | 'assignments' | 'documents'
type BoxTruckVehicleSaveFailureReason = 'validation' | 'conflict' | 'server' | 'network'
type BoxTruckVehicleDocumentOutcome = 'none' | 'success' | 'partial' | 'failed'
export type BoxTruckVehicleSaveAttempt = 'initial' | 'retry'
type BoxTruckVehicleAssignmentOutcome = 'success' | 'partial'

export const classifyBoxTruckVehicleSaveFailure = (error: unknown): BoxTruckVehicleSaveFailureReason => {
    const reason = classifyBoxTruckSaveFailure(error)
    return reason === 'claim_taken' ? 'conflict' : reason
}

export const boxTruckVehicleDocumentOutcome = (attempted: number, failed: number): BoxTruckVehicleDocumentOutcome => {
    if (attempted <= 0) return 'none'
    if (failed <= 0) return 'success'
    return failed >= attempted ? 'failed' : 'partial'
}

/** Which vehicle form a save, open or cancel came from. The whole editor is `full` on the Assets page and in the modal alike. */
const VEHICLE_FORM: Record<TruckModalVariant, string> = { full: 'full', quickAdd: 'quick_add', driverEquipment: 'driver_equipment' }

export const trackBoxTruckVehicleDetailsSaved = (
    method: BoxTruckVehicleSaveMethod,
    input: {
        documentOutcome: BoxTruckVehicleDocumentOutcome
        saveAttempt: BoxTruckVehicleSaveAttempt
        assignmentOutcome: BoxTruckVehicleAssignmentOutcome
        form: TruckModalVariant
    }
): void =>
    fire(AnalyticsEvent.BOX_TRUCK_VEHICLE_DETAILS_SAVED, {
        method,
        document_outcome: input.documentOutcome,
        save_attempt: input.saveAttempt,
        assignment_outcome: input.assignmentOutcome,
        form: VEHICLE_FORM[input.form],
    })

export const trackBoxTruckVehicleDetailsSaveFailed = (
    method: BoxTruckVehicleSaveMethod,
    stage: BoxTruckVehicleSaveFailureStage,
    reason: BoxTruckVehicleSaveFailureReason,
    form: TruckModalVariant
): void => fire(AnalyticsEvent.BOX_TRUCK_VEHICLE_DETAILS_SAVE_FAILED, { method, stage, reason, form: VEHICLE_FORM[form] })

export const trackBoxTruckVehicleFormOpened = (form: TruckModalVariant, method: BoxTruckVehicleSaveMethod, outcome: 'opened' | 'not_found' = 'opened'): void =>
    fire(AnalyticsEvent.BOX_TRUCK_VEHICLE_FORM_OPENED, { form: VEHICLE_FORM[form], method, outcome })

export const trackBoxTruckVehicleFormCancelled = (form: TruckModalVariant, method: BoxTruckVehicleSaveMethod): void =>
    fire(AnalyticsEvent.BOX_TRUCK_VEHICLE_FORM_CANCELLED, { form: VEHICLE_FORM[form], method })

export const trackBoxTruckPageViewed = (): void => fire(AnalyticsEvent.BOX_TRUCK_PAGE_VIEWED)

/** A Search was submitted from the filter bar. Records the filter SHAPE (leg types, whether dates/radius/
 *  equipment are set, bid-loads mode) — never the actual cities/dates. */
export const trackBoxTruckSearchSubmitted = (input: {
    origin: LocationData | null
    destination: LocationData | null
    originRadius: number | null
    destinationRadius: number | null
    startDate: Date | null
    endDate: Date | null
    equipmentLabels: string[]
    bidLoadsOnly: boolean
    /** Whether the DAT source was switched on for this search. */
    datEnabled: boolean
    /** The DAT subtype picks, read against the equipment groups this search actually covers. */
    datEquipmentTypes: string[]
    /** How many ORIGIN points this search runs on — one lane each. Without it a twelve-ZIP search, which
     *  costs twelve SSE streams, Sylectus queries and DAT calls, is indistinguishable here from one city. */
    originPoints: number
    capacityLimits: BoxTruckCapacityLimits
    /** The request's own freshness bound. A closed set (BOX_TRUCK_MAX_POSTED_AGE_OPTIONS_HOURS), so it is
     *  recorded as the number itself. */
    maxPostedAgeHours: number
    /** Whether the search went over the search stream (the `boxTruckStreamSearch` arm) rather than `search-unified`. */
    viaSearchStream: boolean
}): void =>
    fire(AnalyticsEvent.BOX_TRUCK_SEARCH_SUBMITTED, {
        search_arm: searchArm(input.viaSearchStream),
        origin_type: legType(input.origin),
        dest_type: legType(input.destination),
        has_dates: !!(input.startDate || input.endDate),
        has_radius: !!(input.originRadius || input.destinationRadius),
        equipment_count: bucketCount(input.equipmentLabels.length),
        origin_count: bucketLanes(input.originPoints),
        bid_loads: input.bidLoadsOnly,
        // Without these, a board that returned nothing because DAT was dropped or because a custom limit cut
        // the rows is indistinguishable in Amplitude from a lane that simply had no freight. Closed sets only.
        dat_enabled: input.datEnabled,
        dat_selection: datSelectionState(input.equipmentLabels, input.datEquipmentTypes),
        capacity_customized: !isDefaultBoxTruckCapacityLimits(input.capacityLimits),
        // Which age the search ran on, so a thin board can be told apart from a tight bound — and so we can
        // see whether operators ever move off the default at all.
        max_age_hours: sanitizeBoxTruckMaxPostedAgeHours(input.maxPostedAgeHours),
    })

/** The endpoint a search ran on: the two arms of the `boxTruckStreamSearch` A/B. Bounded. */
const searchArm = (viaSearchStream: boolean): 'search_stream' | 'search_unified' => (viaSearchStream ? 'search_stream' : 'search_unified')

/** How a Search ended: `success` when any lane delivered, `refused` when the endpoint answered with a 4xx (quota, access), `error` otherwise. */
type BoxTruckSearchOutcome = 'success' | 'error' | 'refused'

/**
 * How a Search the operator pressed ended, per arm. Fired once per Search, when its lanes have settled — not for a
 * page, a refresh or a reveal. Without it the two arms could be compared on what was searched but not on whether it worked.
 */
export const trackBoxTruckSearchOutcome = (viaSearchStream: boolean, outcome: BoxTruckSearchOutcome): void =>
    trackBoxTruckUiInteraction('search_outcome', outcome, { arm: searchArm(viaSearchStream) })

/** `none` drops the DAT leg, `all` sends no code list, `partial` narrows it — the three shapes a search takes. */
const datSelectionState = (equipmentLabels: string[], codes: string[]): 'none' | 'partial' | 'all' => {
    const { requested, narrowed } = boxTruckDatSelection(equipmentLabels, codes)
    if (requested.length === 0) return 'none'
    return narrowed ? 'partial' : 'all'
}

/** A load's detail drawer was opened. */
export const trackBoxTruckLoadOpened = (load: CanonicalLoad, opts: { hasBids: boolean; hasRating: boolean }): void =>
    fire(AnalyticsEvent.BOX_TRUCK_LOAD_OPENED, { source: sourceGroupKey(load), has_bids: opts.hasBids, has_rating: opts.hasRating })

export const trackBoxTruckEmailSent = (props: { email_type: BoxTruckEmailType; source: string; pricing_shown?: boolean }): void =>
    fire(AnalyticsEvent.BOX_TRUCK_EMAIL_SENT, props)

/**
 * How the pricing panel resolved for one load.
 *
 * The pilot's whole question is whether the panel is useful, and that cannot be read off the adherence
 * page alone: it only sees loads where a bid was sent. This counts the denominator — how often a price is
 * shown at all, how often there is no model, how often an equipment class is refused, and how quote
 * errors trend. Fired where the quote SETTLES, which is keyed per priced load, so a live-board re-render
 * does not count twice.
 */
export const trackBoxTruckPricingQuote = (outcome: 'ready' | 'no-model' | 'unpriceable' | 'error', equipment?: string, confidence?: string): void =>
    trackBoxTruckUiInteraction('pricing_quote', [outcome, equipment ?? '-', confidence ?? '-'].join(':'))

/** What became of a report the dispatcher started writing. Not exported: it is the parameter of one
 *  function in this file and nothing imports the name. */
type BoxTruckPricingReportOutcome = 'sent' | 'failed' | 'cancelled'

/**
 * A dispatcher opened the report dialog, and what came of it.
 *
 * The complaint itself goes to Telegram and is never stored, so this is the only countable trace of it.
 * The OUTCOME is here and not just the successes, because nothing is persisted: a Telegram outage, an
 * unconfigured group or a tenant whose model went missing would make the panel's one feedback channel
 * silently useless, and `sent`-only counting cannot tell that apart from nobody having a complaint.
 * `cancelled` is the third real answer — someone opened it, thought again, and closed it.
 *
 * "How often is the panel argued with" is now `outcome = sent`. The REASON is here and the TEXT is not,
 * and the difference is bounded-ness: the reason comes from the contract's own ten-item list, so the
 * series it opens can be counted in advance, while a free-text dimension is unbounded and would carry a
 * customer's own words into a metric label. Which KIND of wrongness the desk reports most is the one
 * thing this channel can say about where the model should change next, and a Telegram scroll cannot
 * answer it.
 *
 * The reason is its OWN property and not a fourth segment of `detail`, for the same reason the elements
 * above keep their original names: anything already filtering on `sent:CARGO_VAN:high` would have gone
 * to zero the day a segment was appended, with no gap to say why. A property is also the shape you want
 * it in — grouping by `reason` beats parsing a composite string.
 */
export const trackBoxTruckPricingReported = (
    outcome: BoxTruckPricingReportOutcome,
    equipment?: string,
    confidence?: string,
    reason?: BoxTruckPricingReportReason
): void => trackBoxTruckUiInteraction('pricing_reported', [outcome, equipment ?? '-', confidence ?? '-'].join(':'), reason ? { reason } : undefined)

/** A team star rating was set. `rating` is 1–5, or 0 when cleared. */
export const trackBoxTruckRatingSet = (rating: number): void => fire(AnalyticsEvent.BOX_TRUCK_RATING_SET, { rating, cleared: rating === 0 })

export const trackBoxTruckNoteSaved = (): void => fire(AnalyticsEvent.BOX_TRUCK_NOTE_SAVED)

/**
 * Smaller UI-control interactions (toggles, chips, copy, map, refresh). One event; group by `element`.
 *
 * `props` is for a dimension that does not belong inside `detail`: a value a chart should GROUP by, or
 * one added to an element whose `detail` is already a series people filter on. It goes through here
 * rather than through `fire` at the call site so this stays the only path to this event — a second one
 * would quietly skip whatever is added here later (sanitising, a shared property, a rename).
 */
export const trackBoxTruckUiInteraction = (element: BoxTruckUiElement, detail?: string, props?: FireProps): void =>
    // Props FIRST, like `fire` itself: the event's identity wins, so a caller cannot overwrite `element`
    // or `detail` from the bag and leave the series pointing at something else.
    fire(AnalyticsEvent.BOX_TRUCK_UI_INTERACTION, { ...props, element, ...(detail !== undefined ? { detail } : {}) })

/**
 * An operator changed the Nearby Drivers order (`detail`: `distance` or `recent`), and how page 1 of that order
 * ended. ONE per click: the click arms it and its own request settles it, so a bid or origin refresh never
 * sends one. Which order operators pick is `detail` over all; how often a re-sort fails is `outcome`.
 */
export const trackBoxTruckNearbySortChanged = (
    sort: BoxTruckNearbyDriversSort,
    surface: BoxTruckNearbyDriversSurface,
    outcome: BoxTruckNearbySortOutcome
): void => trackBoxTruckUiInteraction('nearby_sort_changed', sort, { surface, outcome })

export const trackBoxTruckCapacityLimitChanged = ({ groupId, field }: BoxTruckCapacityInteraction): void =>
    trackBoxTruckUiInteraction('capacity_limit_changed', `${groupId}:${field}`)

export const trackBoxTruckDatSubtypesChanged = ({ groupId, action, selection }: BoxTruckDatSubtypeInteraction): void =>
    trackBoxTruckUiInteraction('dat_subtypes_changed', `${groupId}:${action}:${selection}`)

/**
 * Whose paperwork a document strip shows. Bounded by construction — the label a chip carries is never a
 * metric value, and neither is the file name.
 */
export type BoxTruckDocumentScope = 'driver' | 'owner' | 'vehicle'

const PROFILE_DOCUMENT_TYPES = new Set<string>([...BOX_TRUCK_PROFILE_DRIVER_DOC_TYPES, ...BOX_TRUCK_OWNER_DOC_TYPES, ...BOX_TRUCK_ASSET_DOC_TYPES])
const PROTECTED_PROFILE_DOCUMENT_TYPES = new Set<string>(BOX_TRUCK_PROTECTED_DOC_TYPES)

const trackBoxTruckProfileDocumentLifecycle = (
    action: AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_UPLOAD | AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_REMOVAL,
    entityType: BoxTruckDocumentScope,
    documentType: string,
    outcome: 'success' | 'failure',
    extra: Record<string, string> = {}
): void => {
    const scope = documentScope(entityType, documentType)
    if (scope) fire(action, { ...scope, outcome, ...extra })
}

/** Whose document and which type, or null for a driver's protected identity document (never reported). */
const documentScope = (entityType: BoxTruckDocumentScope, documentType: string): { entity_type: string; document_type: string } | null =>
    entityType === 'driver' && PROTECTED_PROFILE_DOCUMENT_TYPES.has(documentType)
        ? null
        : { entity_type: entityType, document_type: PROFILE_DOCUMENT_TYPES.has(documentType) ? documentType : 'unknown' }

/** How one file's AI read ended: `facts` when it was read as the card's type. */
export type BoxTruckAutofillReadOutcome = 'facts' | AutofillProblem

/** The names of the fields a read returned with a value, sorted: the extraction contract's keys, never a value. */
const returnedFieldNames = (facts: Record<string, unknown>): string[] =>
    Object.keys(facts)
        .filter((key) => key !== 'isReadable' && key !== 'confidence')
        .filter((key) => {
            const value = facts[key]
            return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== ''
        })
        .sort()

/** A doc type the pipeline answered with: its registry names are snake_case; anything else is not reported as is. */
const pipelineDocType = (docType: string | undefined): string => (docType === undefined ? 'none' : /^[a-z0-9_]{1,40}$/.test(docType) ? docType : 'unknown')

/** One file's AI read: the card's type, what the model read it as, and which fields it returned (names only). */
export const trackBoxTruckProfileDocumentAutofillRead = (
    entityType: BoxTruckDocumentScope,
    documentType: string,
    read: {
        outcome: BoxTruckAutofillReadOutcome
        readAs?: string
        cached?: boolean
        facts?: Record<string, unknown>
        attempt: number
        durationMs: number
    }
): void => {
    const scope = documentScope(entityType, documentType)
    if (!scope) return
    const fields = read.facts ? returnedFieldNames(read.facts) : []
    fire(AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_AUTOFILL_READ, {
        ...scope,
        outcome: read.outcome,
        read_as: pipelineDocType(read.readAs),
        cached: Boolean(read.cached),
        attempt: read.attempt,
        duration_ms: Math.round(read.durationMs),
        fields_returned: fields.join(','),
        fields_returned_count: fields.length,
    })
}

/** A card's read put into the form: how many fields it filled, offered beside HR's value, left to HR because files disagree, or could not write. */
export const trackBoxTruckProfileDocumentAutofillApplied = (
    entityType: BoxTruckDocumentScope,
    documentType: string,
    result: { files: number; problems: number; found: number; filled: number; differs: number; conflicts: number; locked: number }
): void => {
    const scope = documentScope(entityType, documentType)
    if (scope) fire(AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_AUTOFILL_APPLIED, { ...scope, ...result })
}

/**
 * HR starting, cancelling or retrying a card's read, using a document's value for a field (`field` is the form key, never the value),
 * or taking off the card a file read as another document (`readAs`, and whether that came from an earlier read of the same file).
 */
export const trackBoxTruckProfileDocumentAutofillAction = (
    entityType: BoxTruckDocumentScope,
    documentType: string,
    action: 'start' | 'cancel' | 'retry' | 'use' | 'remove_wrong_file',
    { readAs, cached, ...detail }: { files?: number; field?: string; readAs?: string; cached?: boolean } = {}
): void => {
    const scope = documentScope(entityType, documentType)
    if (!scope) return
    const props = { ...scope, autofill_action: action, ...detail }
    if (action !== 'remove_wrong_file') return fire(AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_AUTOFILL_ACTION, props)
    fire(AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_AUTOFILL_ACTION, { ...props, read_as: pipelineDocType(readAs), cached: Boolean(cached) })
}

export const trackBoxTruckProfileDocumentRemoval = (entityType: BoxTruckDocumentScope, documentType: string, outcome: 'success' | 'failure'): void =>
    trackBoxTruckProfileDocumentLifecycle(AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_REMOVAL, entityType, documentType, outcome)

/**
 * One upload's outcome, with the road it took and, on failure, the step it stopped at.
 *
 * `route` is decided by size, the same test the upload makes. `failed_step` is `upload_url`, `storage` or
 * `confirm` on the direct road and `upload` on the proxied one: a storage CORS or bucket fault and a confirm
 * outage would otherwise both read as `failure`, which is how the original "Network Error" stayed invisible.
 */
export const withBoxTruckProfileDocumentUploadAnalytics = async <T>(
    entityType: BoxTruckDocumentScope,
    documentType: string,
    file: Pick<File, 'size'>,
    upload: () => Promise<T>
): Promise<T> => {
    const route = needsDirectBoxTruckDocumentUpload(file) ? 'direct' : 'proxied'
    try {
        const result = await upload()
        trackBoxTruckProfileDocumentLifecycle(AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_UPLOAD, entityType, documentType, 'success', { route })
        return result
    } catch (error) {
        const failedStep = route === 'direct' ? (boxTruckDirectUploadFailedStep(error) ?? 'unknown') : 'upload'
        trackBoxTruckProfileDocumentLifecycle(AnalyticsEvent.BOX_TRUCK_PROFILE_DOCUMENT_UPLOAD, entityType, documentType, 'failure', {
            route,
            failed_step: failedStep,
        })
        throw error
    }
}

/** Bounded: the direct-upload threshold (30 MB) and the limit (50 MB) are the edges that matter, plus twice the limit. */
const skippedSizeBucket = (bytes: number): string => {
    if (bytes <= BOX_TRUCK_DOC_DIRECT_UPLOAD_MIN_BYTES) return 'under_30mb'
    if (bytes <= BOX_TRUCK_DOC_MAX_BYTES) return '30_50mb'
    return bytes <= BOX_TRUCK_DOC_MAX_BYTES * 2 ? '50_100mb' : 'over_100mb'
}

/** A picked profile document was refused before upload — too large, or a type that is not accepted. */
export const trackBoxTruckProfileDocumentSkipped = (file: Pick<File, 'type' | 'size'>): void => {
    const reason = boxTruckDocumentRejectionReason(file)
    if (reason) trackBoxTruckUiInteraction('profile_document_skipped', `${reason}:${skippedSizeBucket(file.size)}`)
}

/** Which kind of record an owner was given. */
export type BoxTruckOwnerLinkEntity = 'driver' | 'vehicle'

/**
 * An owner was given a driver or vehicle that already existed.
 *
 * `reassigned` carries the question this event exists to answer: the picker deliberately offers records
 * that already belong to another carrier, labelled as such, so knowing how often operators take one is
 * how we learn whether that was the right call. Counts are bucketed and nothing names the record or
 * either owner.
 */
export const trackBoxTruckOwnerLinked = (entityType: BoxTruckOwnerLinkEntity, input: { count: number; reassigned: boolean }): void =>
    fire(AnalyticsEvent.BOX_TRUCK_OWNER_LINKED, { entityType, count: bucketCount(input.count), reassigned: input.reassigned })

/**
 * The same round failed, wholly or in part.
 *
 * `linked` is not decoration: links are sent one at a time, so a failure at the third of five has already
 * moved two records. A failure rate that hides partial rounds would read as "nothing happened".
 */
export const trackBoxTruckOwnerLinkFailed = (entityType: BoxTruckOwnerLinkEntity, input: { attempted: number; linked: number }): void =>
    fire(AnalyticsEvent.BOX_TRUCK_OWNER_LINK_FAILED, {
        entityType,
        attempted: bucketCount(input.attempted),
        linked: bucketCount(input.linked),
        partial: input.linked > 0,
    })

// ─── SMS over the tenant's Quo line (ADR-043) ───────────────────────────────

/** Where a send came from: the inbox composer, the roster's bulk picker, or a scheduled mailing's form. */
export type BoxTruckSmsSurface = 'inbox' | 'bulk' | 'mailing' | 'load_drawer'
/** How a send ended. */
/** `unconfirmed`: handed to Quo with no confirmation back — a rate worth watching on its own, because
 *  it is the one outcome where the operator is told NOT to try again.
 *  `queued`: too many recipients to send inside the request, so it was written down for the tick to
 *  drain. Not a success and not a failure yet, and conflating it with `sent` would make the bulk
 *  surface look like it delivers thousands in a second. */
type BoxTruckSmsOutcome = 'sent' | 'partial' | 'none' | 'unconfirmed' | 'queued'

/**
 * How big a send was, where that is the question — `bucketCount` collapses everything past one into
 * `2+`, which cannot tell a reply from a fleet-wide broadcast.
 *
 * The first threshold is `SMS_MAX_IMMEDIATE_RECIPIENTS`, imported rather than written out: it is where
 * a send stops going out inside the request and starts being queued, and a copy of it here would
 * mislabel every send the day that limit moves. The two above it are multiples of it — a crew, and
 * everybody — and the labels are built from the same numbers so they cannot disagree either.
 */
const RECIPIENT_BUCKETS = [SMS_MAX_IMMEDIATE_RECIPIENTS, SMS_MAX_IMMEDIATE_RECIPIENTS * 4, SMS_MAX_IMMEDIATE_RECIPIENTS * 16] as const

/**
 * Exported for the labels a PRESET writes, which name a count too.
 *
 * A raw `ids.length` in an event label gives every fleet size its own value — `sms:all:ok:808`,
 * `sms:all:ok:809` — and the question the event exists for ("is the gap preset ever pressed") cannot
 * be asked of a label with a thousand values.
 */
export const bucketRecipients = (n: number): string => {
    if (n <= 1) return '1'
    let from = 2
    for (const edge of RECIPIENT_BUCKETS) {
        if (n <= edge) return `${from}-${edge}`
        from = edge + 1
    }
    return `${from}+`
}

/** Quo connected. `lines` is how many numbers the workspace turned out to have, not which. */
export const trackBoxTruckQuoConnected = (lines: number): void => fire(AnalyticsEvent.BOX_TRUCK_QUO_CONNECTED, { lines: bucketCount(lines) })

/** Connect refused. The reason is bucketed — a rejected key, or anything else. */
export const trackBoxTruckQuoConnectFailed = (reason: 'rejected_key' | 'no_lines' | 'server'): void =>
    fire(AnalyticsEvent.BOX_TRUCK_QUO_CONNECT_FAILED, { reason })

export const trackBoxTruckQuoDisconnected = (): void => fire(AnalyticsEvent.BOX_TRUCK_QUO_DISCONNECTED)

/** The reply webhook was re-registered by hand, which only happens after a connect could not finish it. */
export const trackBoxTruckQuoWebhookRetried = (outcome: 'success' | 'failure'): void => fire(AnalyticsEvent.BOX_TRUCK_QUO_WEBHOOK_RETRIED, { outcome })

/** The routing screen saved: how many people were given a line of their own, and whether the default moved. */
export const trackBoxTruckQuoRoutingSaved = (input: { overrides: number; fallbackChanged: boolean }): void =>
    fire(AnalyticsEvent.BOX_TRUCK_QUO_ROUTING_SAVED, { overrides: bucketCount(input.overrides), fallback_changed: input.fallbackChanged })

export const trackBoxTruckQuoRoutingSaveFailed = (reason: 'validation' | 'server'): void => fire(AnalyticsEvent.BOX_TRUCK_QUO_ROUTING_SAVE_FAILED, { reason })

/**
 * An SMS send the operator started. Recipients are bucketed and nothing identifying the driver or the
 * message is sent — this answers "is the feature used, and does it work", not who was texted.
 *
 * WHERE A QUEUED SEND'S OUTCOME IS MEASURED, and deliberately not here. This event reports that the
 * run was ACCEPTED (`outcome: 'queued'`); how it ended is answered on the server, by the
 * `[box-truck-broadcast] run finished` log line, which carries `outcome` (`done` / `abandoned`),
 * `service`, whether it was `scheduled`, and the totals — one line per run, emitted where the status
 * is decided. The abandonment rate is that line grouped by `outcome`.
 *
 * It is not fired from this page because the page cannot see it: a run leaves over the following
 * minutes, a fleet-sized one takes fifteen to fifty, and a scheduled mailing's run starts with nobody
 * watching at all. An event from the progress strip would miss precisely the runs worth counting and
 * report an abandonment rate far below the truth.
 */
export const trackBoxTruckSmsSent = (input: {
    surface: BoxTruckSmsSurface
    recipients: number
    outcome: BoxTruckSmsOutcome
    mine?: boolean
    /** The sender chose a line other than their own — see `custom_sender_line` below. */
    customSenderLine?: boolean
}): void =>
    fire(AnalyticsEvent.BOX_TRUCK_SMS_SENT, {
        surface: input.surface,
        recipients: bucketCount(input.recipients),
        // Kept beside the old property rather than replacing it: `recipients` has history in every
        // existing chart, and this is the one that can see a broadcast.
        recipient_count: bucketRecipients(input.recipients),
        outcome: input.outcome,
        // Whose conversation it went into, where the surface knows. Answering from a colleague's
        // thread sends from THEIR number and their badge gets the reply, which is the risk the
        // drawer's default is built around — unmeasured, it is only an argument.
        ...(input.mine === undefined ? {} : { mine: input.mine }),
        // Whether the operator sent as a desk other than their own. The id is NOT reported: which
        // colleague's number it was is the membership fact the picker deliberately does not show, and
        // an analytics property is still a disclosure. Adoption and a wrong-line failure rate are
        // answerable from the flag alone.
        ...(input.customSenderLine === undefined ? {} : { custom_sender_line: input.customSenderLine }),
    })

/**
 * An attachment the browser could not render — the one failure this surface has, and the only way to
 * learn whether the vendor's signed links expire.
 *
 * The decision it feeds is real: if these climb with message age, the answer is a proxy or a copy
 * into our own bucket, and without the event that argument has no numbers. The MIME type goes with
 * it; the URL never does, since a signed link is a key to a driver's document.
 */
export const trackBoxTruckSmsAttachmentFailed = (kind: 'image' | 'audio', mimeType: string | null): void =>
    fire(AnalyticsEvent.BOX_TRUCK_SMS_ATTACHMENT_FAILED, { surface: 'sms', kind, mimeType: mimeType ?? 'unknown' })

/** The drawer's panel was switched to another of this driver's conversations, and whose it was. */
export const trackBoxTruckSmsLineSwitched = (mine: boolean): void => fire(AnalyticsEvent.BOX_TRUCK_SMS_LINE_SWITCHED, { surface: 'load_drawer', mine })

/**
 * The load drawer's SMS panel was opened for a driver, and what it found.
 *
 * Opening to READ is most of what this panel is for, and sends alone cannot see it: a dispatcher who
 * checks a thread and does not reply is a use, and `no_number` / `failed` are the two ways the entry
 * point disappoints — neither visible from the send events.
 */
export const trackBoxTruckSmsChatOpened = (outcome: 'thread' | 'no_thread' | 'no_number' | 'failed', entry: BoxTruckSmsChatEntry): void =>
    fire(AnalyticsEvent.BOX_TRUCK_SMS_CHAT_OPENED, { surface: 'load_drawer', outcome, entry })

/**
 * Which button opened it. A property rather than an event of its own: the panel fires the open exactly
 * once however it was reached, so a second event beside it would count the same press twice.
 */
export type BoxTruckSmsChatEntry = 'bid' | 'nearby'

/**
 * The load bot was switched, and whether the switch took. No driver id and no desk.
 *
 * `on` is the state ASKED for; `outcome` exists because this control fails silently otherwise, leaving
 * "the bot stopped answering" indistinguishable from "an operator turned it off".
 *
 * `applied` (conversation scope only) compares the ask with the server's answer: a request to turn one back
 * on that comes back off is a driver's own STOP, which only they may undo (ADR-057 §14).
 */
export const trackBoxTruckOfferBotToggled = (input: {
    scope: 'conversation' | 'company'
    surface: 'conversation_bar' | 'load_drawer'
    on: boolean
    outcome: 'success' | 'failure'
    applied?: boolean
}): void =>
    fire(AnalyticsEvent.BOX_TRUCK_OFFER_BOT_TOGGLED, {
        scope: input.scope,
        surface: input.surface,
        on: input.on,
        outcome: input.outcome,
        ...(input.applied === undefined ? {} : { applied: input.applied }),
    })

export const trackBoxTruckSmsSendFailed = (surface: BoxTruckSmsSurface, reason: BoxTruckSmsFailureReason): void =>
    fire(AnalyticsEvent.BOX_TRUCK_SMS_SEND_FAILED, { surface, reason })

type BoxTruckSmsFailureReason = 'not_connected' | 'quo_refused' | 'rate_limited' | 'validation' | 'server'

/**
 * Why a send failed, from the response STATUS.
 *
 * It used to read the user-facing sentence and call anything containing "Quo" a connection problem —
 * which is most of them, including "rejected the API key", "is rate-limiting this workspace" and
 * "refused the request". The one number this event exists to produce was therefore wrong. The server
 * already says it properly: 409 is something the tenant must fix in Quo, 429 is pace, 4xx is the
 * request, and anything else is ours.
 */
export const smsFailureReason = (error: unknown): BoxTruckSmsFailureReason => {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    if (status === 409) return 'not_connected'
    if (status === 429) return 'rate_limited'
    if (status === 502 || status === 503) return 'quo_refused'
    if (status && status >= 400 && status < 500) return 'validation'
    return 'server'
}

/** Which service a mailing is scheduled on — the Telegram/SMS split, per saved mailing. */
export const trackBoxTruckMailingServiceChosen = (service: 'telegram' | 'sms', customSenderLine = false): void =>
    // `custom_sender_line` only means "not the creator's own". Which line it was is membership the
    // picker itself refuses to show, and an analytics property discloses it just as well.
    fire(AnalyticsEvent.BOX_TRUCK_MAILING_SERVICE_CHOSEN, { service, custom_sender_line: customSenderLine })

/** A pasted ZIP list was added in one press. Counts only, never the ZIPs. */
export const trackBoxTruckZipListAdded = (input: { added: number; notFound: number; failed: number; ignored: number }): void =>
    fire(AnalyticsEvent.BOX_TRUCK_ZIP_LIST_ADDED, {
        added: bucketLanes(input.added),
        // Not bucketed: these are the small numbers that say whether the feature is misbehaving, and a
        // "2+" that covers both one bad ZIP and ten provider failures answers neither question.
        not_found: input.notFound,
        failed: input.failed,
        ignored: input.ignored,
    })
